import React, { useState, useEffect } from "react";
import { useParams, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { TrophyIcon } from "@heroicons/react/24/outline";
import DashboardLayout from "../../components/DashboardLayout";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import AudienceHeaderIcon from "../../components/audiences/AudienceHeaderIcon";
import AudienceSwitcher from "../../components/audiences/AudienceSwitcher";
import { audiencesService } from "../../services/audiences";
import Patterns from "./Patterns";
import Highlights from "./Highlights";
import Analytics from "./Analytics";
import Creators from "./Creators";
import Videos from "./Videos";
import Settings from "./Settings";
import AudienceApi from "./Api";
import AudiencePopulatingModal from "./AudiencePopulatingModal";
import { getAudienceDisplayName } from "../../constants/audiences";
import ModalRoute from "../../components/common/ModalRoute";
import AgentInstructionsModal from "../../components/api/AgentInstructionsModal";

const AudienceDetail = ({ currentUser }) => {
  const { hash } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [audience, setAudience] = useState(null);
  const [loading, setLoading] = useState(true);
  const populatingRedirectRef = React.useRef(false);

  useEffect(() => {
    populatingRedirectRef.current = false;
    setLoading(true);
    audiencesService.getAudience(
      hash,
      (response) => {
        setAudience(response.data);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );
  }, [hash]);

  useEffect(() => {
    if (!audience || loading) return;
    if (location.pathname.includes("/populating")) return;
    if (populatingRedirectRef.current) return;
    if (!shouldOpenPopulatingModal(audience)) return;

    populatingRedirectRef.current = true;
    navigate(`/audience/${hash}/populating/`, { replace: true });
  }, [audience, loading, hash, location.pathname, navigate]);

  const flags = audienceFeatureFlags(currentUser);
  const moreSubmenu = [];
  if (flags.audience_analytics) {
    moreSubmenu.push({
      id: "analytics",
      label: "Analytics",
      to: `/audience/${hash}/analytics/`,
    });
  }

  const tabs = [
    {
      id: "patterns",
      label: "Patterns",
      path: "patterns",
      hidden: !flags.audience_patterns,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v9a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
        </svg>
      ),
    },
    {
      id: "highlights",
      label: "Highlights",
      path: "highlights",
      hidden: !flags.audience_highlights,
      icon: <TrophyIcon className="w-5 h-5" />,
    },
    {
      id: "analytics",
      label: "Analytics",
      path: "analytics",
      hidden: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      id: "videos",
      label: "Videos",
      path: "videos",
      hidden: !flags.audience_videos,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: "creators",
      label: "Creators",
      path: "creators",
      hidden: !flags.audience_creators,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    {
      id: "api",
      label: "API",
      path: "api",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
        </svg>
      ),
    },
    {
      id: "settings",
      label: "Settings",
      path: "settings",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes("/highlights")) return "highlights";
    if (path.includes("/analytics")) return "analytics";
    if (path.includes("/creators")) return "creators";
    if (path.includes("/videos")) return "videos";
    if (path.includes("/api")) return "api";
    if (path.includes("/settings")) return "settings";
    return "patterns";
  };

  const activeTab = getActiveTabFromPath();
  const moreSubActive =
    moreSubmenu.length > 0 && moreSubmenu.some((s) => s.id === activeTab);

  if (loading) {
    return (
      <DashboardLayout title="Loading..." currentUser={currentUser}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <OrbitalLoader size={128} />
        </div>
      </DashboardLayout>
    );
  }

  if (!audience) {
    return (
      <DashboardLayout title="Not Found" currentUser={currentUser}>
        <div className="text-theme-text">Audience not found</div>
      </DashboardLayout>
    );
  }

  const landingPath = audienceFirstAllowedPath(flags, hash);
  const pathWithoutPatterns = audienceFirstAllowedPath(flags, hash, { skip: "patterns" });
  const pathWithoutVideos = audienceFirstAllowedPath(flags, hash, { skip: "videos" });

  const audiencePageTitle = (
    <span className="flex items-center gap-2 min-w-0">
      <span className="shrink-0 w-4 h-4 flex items-center justify-center leading-none">
        <AudienceHeaderIcon audience={audience} />
      </span>
      <span className="text-lg font-semibold truncate hover:underline">{getAudienceDisplayName(audience)}</span>
      <AudienceSwitcher currentHash={hash} currentAudience={audience} />
    </span>
  );

  return (
    <DashboardLayout
      title={audiencePageTitle}
      subtitle={null}
      currentUser={currentUser}
      navConfig={[
        ...tabs
          .filter((tab) => !tab.hidden)
          .map((tab) => ({
            id: tab.id,
            label: tab.label,
            icon: tab.icon,
            mobileIconOnly: false,
            onClick: () => navigate(`/audience/${hash}/${tab.path}/`),
            active: activeTab === tab.id,
          })),
        ...(moreSubmenu.length > 0
          ? [
              {
                id: "more",
                label: "More",
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h7"
                    />
                  </svg>
                ),
                mobileIconOnly: false,
                active: moreSubActive,
                submenu: moreSubmenu.map((s) => ({
                  ...s,
                  active: activeTab === s.id,
                })),
              },
            ]
          : []),
      ]}
    >
      <Routes>
        <Route
          path="highlights"
          element={
            <AudienceFeatureRoute enabled={flags.audience_highlights} redirectTo={landingPath}>
              <Highlights audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="highlights/:patternType/:patternId/*"
          element={
            <AudienceFeatureRoute enabled={flags.audience_highlights} redirectTo={landingPath}>
              <Highlights audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="patterns"
          element={
            <AudienceFeatureRoute enabled={flags.audience_patterns} redirectTo={pathWithoutPatterns}>
              <Patterns audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="patterns/:patternType"
          element={
            <AudienceFeatureRoute enabled={flags.audience_patterns} redirectTo={pathWithoutPatterns}>
              <Patterns audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="patterns/:patternType/:patternId/*"
          element={
            <AudienceFeatureRoute enabled={flags.audience_patterns} redirectTo={pathWithoutPatterns}>
              <Patterns audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="analytics"
          element={
            <AudienceFeatureRoute enabled={flags.audience_analytics} redirectTo={landingPath}>
              <Analytics audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="creators"
          element={
            <AudienceFeatureRoute enabled={flags.audience_creators} redirectTo={landingPath}>
              <Creators audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="creators/:creatorHash/"
          element={
            <AudienceFeatureRoute enabled={flags.audience_creators} redirectTo={landingPath}>
              <Creators audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="videos"
          element={
            <AudienceFeatureRoute enabled={flags.audience_videos} redirectTo={pathWithoutVideos}>
              <Videos audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="videos/:videoHash/"
          element={
            <AudienceFeatureRoute enabled={flags.audience_videos} redirectTo={pathWithoutVideos}>
              <Videos audience={audience} />
            </AudienceFeatureRoute>
          }
        />
        <Route
          path="api/*"
          element={<AudienceApi audience={audience} hasAccess={flags.audience_api} />}
        />
        <Route path="settings/*" element={<Settings audience={audience} onAudienceUpdated={setAudience} />} />
        <Route path="populating/" element={<AudiencePopulatingModal landingPath={landingPath} />} />
        <Route path="" element={<Navigate to={landingPath} replace />} />
        <Route path="*" element={<Navigate to={landingPath} replace />} />
      </Routes>
      <ModalRoute
        path={`/audience/${hash}/api/agent-instructions`}
        parentPath={`/audience/${hash}/api/`}
        element={
          <AgentInstructionsModal onClose={() => navigate(`/audience/${hash}/api/`)} />
        }
        maxWidth="max-w-2xl"
        heightClass="h-[90vh] max-h-[90vh]"
        noPadding
      />
    </DashboardLayout>
  );
};

export default AudienceDetail;

function shouldOpenPopulatingModal(audience) {
  return !(audience?.high_fit_video_count > 0);
}

function audienceFeatureFlags(currentUser) {
  const f = currentUser?.features ?? {};
  return {
    audience_patterns: f.audience_patterns !== false,
    audience_highlights: f.audience_highlights !== false,
    audience_videos: f.audience_videos !== false,
    audience_creators: Boolean(f.audience_creators),
    audience_analytics: Boolean(f.audience_analytics),
    audience_api: Boolean(f.api),
  };
}

/** First main audience tab the user may open (patterns, highlights, videos), else settings. */
function audienceFirstAllowedPath(flags, hash, { skip } = {}) {
  const base = `/audience/${hash}/`;
  const segments = [
    ["patterns", flags.audience_patterns],
    ["highlights", flags.audience_highlights],
    ["videos", flags.audience_videos],
  ];
  for (const [segment, on] of segments) {
    if (segment === skip) continue;
    if (on) return `${base}${segment}/`;
  }
  return `${base}settings/`;
}

function AudienceFeatureRoute({ enabled, redirectTo, children }) {
  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}
